import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext.jsx";

function formatCurrency(cents) {
  return (cents / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

function formatLastSeen(isoString) {
  if (!isoString) return "Never played";
  const diffMs = Date.now() - new Date(isoString).getTime();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 2) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 8) return `${weeks}w ago`;
  return new Date(isoString).toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

const RANK_STYLE = {
  1: { icon: "🥇", text: "text-gold", border: "border-gold" },
  2: { icon: "🥈", text: "text-mist", border: "border-mist" },
  3: { icon: "🥉", text: "text-magenta", border: "border-magenta" },
};

export default function Leaderboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      const { data, error: rpcError } = await supabase.rpc("get_leaderboard", { p_limit: 20 });
      if (cancelled) return;
      if (rpcError) {
        console.error("Failed to load leaderboard:", rpcError.message);
        setError(rpcError.message);
      } else {
        setRows(data || []);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="mx-auto max-w-2xl px-4 py-8">
      <button onClick={() => navigate("/")} className="btn-ghost mb-6 !text-[8px]">
        ◀ BACK TO LEVELS
      </button>

      <div className="mb-6">
        <p className="label-pixel mb-2 text-gold">High Scores</p>
        <h1 className="pixel-heading text-lg text-mist sm:text-2xl">Family Leaderboard</h1>
        <p className="mt-3 text-sm text-mist/60">
          Ranked by Net Worth, earned across every level. Everyone here shares this arcade's
          invite code — this board is visible to your whole family.
        </p>
      </div>

      {error && (
        <div className="panel border-danger p-5 text-sm text-danger">
          Couldn't load the leaderboard right now: {error}
        </div>
      )}

      {!error && rows === null && (
        <div className="panel p-6 text-center">
          <p className="font-display text-[10px] text-cyan">LOADING SCOREBOARD…</p>
        </div>
      )}

      {rows && rows.length === 0 && (
        <div className="panel p-6 text-center text-sm text-mist/60">
          No scores yet — be the first to finish a level.
        </div>
      )}

      {rows && rows.length > 0 && (
        <div className="space-y-2">
          {rows.map((row, i) => {
            const rank = i + 1;
            const isMe = row.id === user?.id;
            const rankStyle = RANK_STYLE[rank];
            return (
              <div
                key={row.id}
                className={`panel flex items-center gap-4 p-4 ${isMe ? "border-cyan shadow-glow-cyan" : ""}`}
              >
                <div className={`w-10 shrink-0 text-center font-display text-sm ${rankStyle?.text || "text-mist/50"}`}>
                  {rankStyle ? rankStyle.icon : `#${rank}`}
                </div>

                <div className="min-w-0 flex-1">
                  <p className="truncate pixel-heading text-xs text-mist">
                    {row.display_name || "Player"}
                    {isMe && <span className="ml-2 text-[9px] text-cyan">(YOU)</span>}
                  </p>
                  <p className="text-[10px] text-mist/50">
                    Level {row.level}
                    {row.current_streak > 0 && <span className="ml-2 text-gold">🔥 {row.current_streak}</span>}
                  </p>
                  <p className="text-[10px] text-mist/30">{formatLastSeen(row.last_active_at)}</p>
                </div>

                <div className="shrink-0 text-right">
                  <p className="font-display text-[11px] text-gold">{formatCurrency(row.net_worth_cents)}</p>
                  <p className="text-[10px] text-mist/40">{row.xp.toLocaleString()} XP</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
